import { MortgageCalculator } from "@component/Mortgage/Calculator/MortgageCalculator";
import { getBankName } from "@component/assets/data/banks";
import { faHome as fadHome, faHomeHeart as fadHomeHeart } from "@awesome.me/kit-3777c2145e/icons/duotone/solid";
import {
	faCalculator as fasCalculator,
	faPhone as fasPhone,
	faPaste as fasPaste,
	faFileSignature as fasFileSignature,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LandingAccordion } from "@landing/components/LandingAccordion";
import { LandingBottom } from "@landing/components/LandingBottom";
import { LandingContainer, LandingHowTo, LandingHowToItem } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Accordion, Col, Row } from "react-bootstrap";

interface Props {
	bank: string;
}

/**
 * @Routing
 *
 * @title "[bank] Lakáshitel Kalkulátor"
 * @description "Tekintsd át az [bank] széleskörű lakáshitel kínálatát! A megfelelő lakáshitel kiválasztása után segítünk a hitel igénylés teljes folyamatában."
 * @route "/:bank(budapestbank|cib|erste|kh|mbh|otp|raiffeisen|unicredit|sberbank)/lakashitel/"
 */
export default class Landing extends React.PureComponent<Props> {
	public render(): ReactNode {
		const { bank } = this.props;

		return (
			<>
				<MortgageCalculator
					title={`${getBankName(bank)} - lakáshitel kalkulátor`}
					description={
						["otp", "kh", "erste", "cib"].includes(bank)
							? `Intézd ${bank.toUpperCase()} lakáshiteled digitális előminősítéssel!`
							: "Spórolj százezreket! Személyre szabott megoldást adunk hiteligényedre."
					}
					highlightedBank={bank}
					initialResult
					enableScrollToInitialResult
				/>
				{this.renderBankInfo()}
				<LandingContainer size="lg" bgClassName="bg-light" className="my-4">
					<Row>
						<Col lg="6" className="pb-5 pb-lg-0">
							<Row className="justify-content-center align-items-center">
								<Col md="3" className="text-center pb-3 pb-md-0">
									<FontAwesomeIcon icon={fadHome} className="text-secondary" size="5x" />
								</Col>
								<Col md="9">
									<h2 className="pension-small-title">Hogyan válasszak lakáshitelt?</h2>
									<p className="mb-3">
										A lakáshitel az egyik legbonyolultabb kölcsön, segítünk megtalálni a számodra
										legkedvezőbb hitelajánlatot.
									</p>
									<a
										href="/hogyan-valasszak-lakashitelt/?link_type=landing-link"
										className="text-underlined"
									>
										<u>Tudj meg többet</u>
									</a>
								</Col>
							</Row>
						</Col>
						<Col lg="6">
							<Row className="justify-content-center align-items-center">
								<Col md="3" className="text-center pb-3 pb-md-0">
									<FontAwesomeIcon icon={fadHomeHeart} className="text-secondary" size="5x" />
								</Col>
								<Col md="9">
									<h2 className="pension-small-title">Hogyan igényelhetek lakáshitelt?</h2>
									<p className="mb-3">
										Megtudhatod a hiteligénylés folyamatát: a megfelelő lépéseket követve gyorsabban
										kaphatod meg a hitelösszeget.
									</p>
									<a href="/lakashitel-igenyles/?link_type=landing-link" className="text-underlined">
										<u>Tudj meg többet</u>
									</a>
								</Col>
							</Row>
						</Col>
					</Row>
				</LandingContainer>
				<LandingHowTo title="Így kaphatod meg a lakáshitelt">
					<LandingHowToItem title="Kalkuláció" icon={<FontAwesomeIcon icon={fasCalculator} />}>
						Versenyeztesd meg a bankok ajánlatait, és válaszd ki a számodra legkedvezőbbet.
					</LandingHowToItem>
					<LandingHowToItem title="Érdeklődés" icon={<FontAwesomeIcon icon={fasPhone} />}>
						Add le érdeklődésed a választott lakáshitelre, és szakértőnk visszahív.
					</LandingHowToItem>
					<LandingHowToItem title="Ügyintézés" icon={<FontAwesomeIcon icon={fasPaste} />}>
						Ha kiválasztottad a számodra megfelelő bankot, segítünk az ügyintézésben is.
					</LandingHowToItem>
					<LandingHowToItem title="Szerződéskötés" icon={<FontAwesomeIcon icon={fasFileSignature} />}>
						A szerződést minden esetben szakértőnk segítségével és az általad kiválasztott bankkal kötöd.
					</LandingHowToItem>
				</LandingHowTo>
				{this.renderFAQ()}
				<LandingBottom tags="lakashitel" />
			</>
		);
	}

	private renderBankInfo(): ReactNode {
		const { bank } = this.props;

		if (bank === "otp") {
			return (
				<LandingContainer>
					<p>
						Az OTP Bank Magyarország legnagyobb pénzintézete, az OTP Csoport tagja. Az OTP Csoport
						Kelet-Közép-Európa egyik meghatározó bankcsoportja:{" "}
					</p>
					<ul>
						<li>11 országban van jelen a bankcsoport.</li>
						<li>Több mint 1400 bankfiók érhető el a régióban.</li>
						<li>Nagyságrendileg 4600 bankautomata (ATM) áll az ügyfelek rendelkezésére.</li>
					</ul>
					<p>
						Természetesen az OTP Banknál jelzáloghitelt is igényelhetsz, melyből számos célodat
						megvalósíthatod legyen szó lakásvásárlásról, felújításról, vagy épp meglévő kölcsönök
						kiváltásáról.
					</p>
				</LandingContainer>
			);
		}

		return null;
	}

	private renderFAQ(): ReactNode {
		const { bank } = this.props;

		if (bank === "otp") {
			return (
				<LandingContainer id="gyakori-kerdesek" sendEvent>
					<Row className="justify-content-center">
						<Col md="10">
							<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">
								Gyakori kérdések az OTP Bank által kínált lakáshitelekről
							</h2>
							<Accordion>
								<LandingAccordion title="Mekkora összegű lakáshitelt igényelhetek?">
									<p>
										Az OTP Banknál a lakáscélra és hitelkiváltási célra igényelt jelzáloghitel
										összege 500 ezer forint és 130 millió forint között lehet. Vagyis kifejezetten
										nagy összegű kölcsönt is kaphatsz az OTP Banktól ingatlancélod megvalósításához.
									</p>
									<p>
										Szabad felhasználású jelzáloghitelt 500 ezer forint és 50 millió forint közötti
										összegben igényelhetsz.
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Milyen futamidőre igényelhetek jelzáloghitelt az OTP Banknál?">
									<p>
										A lakáscélú jelzáloghitelek futamideje 6 és 30 év között lehet. <br />A
										hitelkiváltás céljából igényelt jelzáloghitel esetében a futamidő 11 és 30 év
										között lehet. <br />
										Szabad célra igényelt kölcsön esetében pedig a hitel futamideje 11 és 25 év
										között lehet.
									</p>
									<p>
										A <a href="/jobb-tudni/lakashitel/#lakashitel-futamideje">futamidő</a> nagyon
										lényeges, hiszen hosszabb futamidő mellett a havi törlesztőrészlet is
										alacsonyabb lehet. Ugyanakkor a hosszabb futamidőnek hátránya is van: tovább
										használod a bank pénzét, ezáltal a teljes visszafizetendő összeg is nagyobb
										lesz.
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Milyen kamatperiódus mellett igényelhetek lakáshitelt?">
									<p>
										A kamatfixálás, a{" "}
										<a href="/jobb-tudni/lakashitel/#kamatperiodus">kamatperiódus</a> hossza nagyon
										is lényeges kérdés, ez határozza meg azt, hogy kölcsönöd kamatát a bank milyen
										gyakran módosíthatja. Minél hosszabb a kamatfixálás ideje, annál biztonságosabb
										a hitel, hiszen a kamatot a bank ritkábban változtathatja meg a futamidő alatt.
									</p>
									<p>
										<strong>
											Az OTP Banknál jelenleg kizárólag futamidő végéig fix kamatozás mellett
											igényelhetsz lakáshitelt.
										</strong>{" "}
										Ez a legbiztonságosabb konstrukció, hiszen a kamat az igénylést követően
										egyáltalán nem módosulhat. Lehetnek azonban ennek a megoldásnak hátrányai is:
										nem feltétlenül a végig fix kamat a legolcsóbb,a piaci kamatkörnyezet
										csökkenésekor a kölcsön kamata nem csökken.
									</p>
									<p>
										Találni a piacon 3 hónapos, 6 hónapos, 12 hónapos, 3 éves, 5 éves, 10 éves, 15
										éves kamatperiódus és végig fix kamatozás mellett is lakáskölcsönt. A többi bank
										<a href="/lakashitel-kalkulator/">
											jelzáloghitel ajánlatát itt tudod megnézni.
										</a>
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Milyen törlesztési módok közül választhatok?">
									<p>
										A leggyakoribb a lakáshitelek esetében az egyenlő törlesztőrészlet (úgynevezett{" "}
										<a href="/jobb-tudni/lakashitel/#annuitasos-torlesztoreszlet">
											annuitásos törlesztőrészlet
										</a>
										). Ez azt jelenti, hogy a futamidő alatt a havi fizetnivaló fix - legalábbis
										változatlan kamatszint mellett -, azon belül a tőkerész és kamatrész aránya
										változó. (A futamidő elején több kamatot kell fizetned, míg a lejárathoz
										közeledve egyre nagyobb ütemben csökkented a fennálló tőketartozásodat.)
									</p>
									<p>
										Az OTP Banknál elérhető egy másik törlesztési mód is. Az OTP Évnyerő
										Lakáshiteleket választva az{" "}
										<strong>első 12 hónapban csökkentett törlesztőt kell fizetned.</strong> Ezen
										türelmi időszak alatt ugyanis csak kamatot kell rendezned a pénzintézet felé.
										Ennek a lehetőségnek van előnye és hátránya is. <br />A lakásvásárlást követően
										még sok extra kiadás merülhet fel, ezen időszakban jelenthet nagy előnyt a
										csökkentett törlesztőrészlet. <br />
										Ugyanakkor a türelmi időt követően a havi fizetnivaló valamelyest magasabb lesz,
										mint ha normál fizetési móddal igényelted volna a kölcsönt.
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Milyen célra igényelhetek OTP lakáshitelt?">
									<p>Számos különböző célra igényelhető ingatlanhitel az OTP Banknál:</p>
									<ul>
										<li>Használt lakás, ház vásárlása</li>
										<li>Új építésű lakás, ház vásárlása</li>
										<li>Építési telek vásárlása</li>
										<li>Új lakás, ház építése</li>
										<li>Meglévő ingatlan felújítása, korszerűsítése</li>
										<li>Meglévő lakás, ház bővítése</li>
										<li>Hitelkiváltás (meglévő kölcsön lecserélése egy kedvezőbbre)</li>
										<li>Szabad felhasználásra</li>
									</ul>
									<p>
										A cél pontos meghatározása nagyon fontos, a hitelcél befolyásolhatja a kölcsön
										kamatát, a választható futamidőt és az igényelhető hitelösszeget is.
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Milyen kamat mellett kaphatok lakáshitelt?">
									<p>
										A kölcsön kamata függ a hitelösszeg nagyságától, a jövedelmed összegétől. Sőt
										egyes esetekben a Bankmonitor szakértői akár egyedi kedvezményt is
										kiharcolhatnak számodra.
									</p>
									<p>
										A legjobb hitelajánlat felkutatásával több millió forintot spórolhatsz, éppen
										ezért érdemes összehasonlítani az egyes bankok kölcsöneit. Ehhez ideális eszköz
										lehet a Bankmonitor <a href="/lakashitel-kalkulator/">lakáshitel kalkulátora</a>
										.
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Milyen támogatások érhetők el az OTP Banknál?">
									<p>
										Lakáscélod megvalósításához jelenleg nagy segítséget nyújthatnak a különböző
										lakástámogatások. Ezek közül az alábbiak érhetők el az OTP Banknál:
									</p>
									<ul>
										<li>
											Családi Otthonteremtési Kedvezmény (<a href="/csok-feltetelei/">CSOK</a>),
											amely a meglévő és vállalt gyermekek után járó vissza nem térítendő
											támogatás.
										</li>
										<li>
											A CSOK mellé igényelhető fix 3 százalékos kamatozású{" "}
											<a href="/csok/">támogatott lakáshitel</a>.
										</li>
										<li>
											A CSOK emelt összegű, preferált kistelepüléseken elérhető fajtája a Falusi
											CSOK is igényelhető hazánk legnagyobb pénzintézeténél.
										</li>
										<li>
											A Falusi CSOK mellé felvehető támogatott hitel is megtalálható az OTP Bank
											ajánlatai között.
										</li>
										<li>
											Az új lakásokhoz kapcsolódó adó-visszatérítési támogatást is kihasználhatod,
											ha az OTP-t választod.
										</li>
										<li>
											A <a href="/babavaro-hitel/">Babaváró hitel</a> ugyan nem lakáscélú
											támogatás, de sokan erre a célra fordítják a kapott összeget. Jó hír, hogy
											ez a támogatási forma is elérhető az OTP-nél.
										</li>
									</ul>
									<p>
										Az egyes családtámogatások,{" "}
										<a href="/otthonteremtesi-es-otthonfelujitasi-tamogatasok/">
											lakástámogatások feltételeiről itt olvashatsz
										</a>{" "}
										bővebben.
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Milyen feltételekkel kaphatok lakáshitelt az OTP Banknál?">
									<p>Számos feltételt szükséges teljesíteni a hiteligényléshez.</p>
									<ol>
										<li>
											Szükséges megfelelő munkaviszony. Alkalmazott esetében minimum 3 hónapos
											munkaviszonyt vár el a bank letöltött próbaidővel.
										</li>
										<li>
											Megfelelő jövedelemmel kell rendelkezni:
											<ul>
												<li>
													Minimum havi nettó 154 ezer forintos jövedelem szükséges.
													Természetesen nagyobb hitelösszeg, magasabb törlesztőrészlet esetén
													nem elégséges a minimum szintet teljesíteni. A bevállalható
													törlesztőrészletre vonatkozó szabályokról{" "}
													<a href="/jobb-tudni/lakashitel/#jovedelemaranyos-torlesztoreszlet-mutato-jtm">
														itt olvashatsz
													</a>{" "}
													bővebben.
												</li>
												<li>
													Külföldi jövedelem akkor fogadható el, ha az Ausztriából,
													Németországból, vagy Szlovákiából származik.
												</li>
												<li>
													A nyugdíj is elfogadható jövedelemként. Ebben az esetben nem
													tölthetted be igényléskor a 75. életéved. Ha a hitel lejáratakor már
													betöltenéd a 75. életéved, akkor további biztosíték bevonását várja
													el a bank.
												</li>
											</ul>
										</li>
										<li>
											Szükséges a kölcsön igényléséhez egy megfelelő ingatlanfedezet. Ez
											jellemzően az a lakás, vagy ház szokott lenni, amelyet megvásárolnál,
											építenél, felújítanál. De természetesen más ingatlan is elfogadható lehet.
										</li>
									</ol>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Mi az az önerő?">
									<p>
										Az önerő a lakáscélod megvalósításához minimálisan szükséges saját megtakarítást
										jelenti. Alapesetben ugyanis nem finanszírozhatod meg a célodat teljes egészében
										lakáshitelből.
									</p>
									<p>
										Az OTP Bank <strong>minimálisan 20% önerőt vár el egy lakásvásárlásnál</strong>,
										építkezésnél. De egyes helyzetekben ennél magasabb önerőt is kérhet a
										pénzintézet.
									</p>
									<p>
										Önerőnek minősülhetnek bizonyos esetekben a lakáscélú támogatások közül a CSOK
										és a Babaváró összege is. Arról{" "}
										<a href="/mediatar/cikk/lakasvasarlas-epitkezes-mi-lehet-onero-es-mi-nem/">
											itt olvashatsz, hogy mi minősülhet önerőnek
										</a>
										.
									</p>
									<p>
										Akár önerő nélkül is megveheted álmaid otthonát, ha okosan használod ki a
										lakástámogatásokat. Vagy, ha megfelelő értékű pótfedezetet is felajánlasz a
										jelzáloghitel mögé. Ez utóbbi esetben ugyanis a két ingatlan együttes értékének
										kell elbírnia a kért hitelösszeget.
									</p>
								</LandingAccordion>
							</Accordion>
							<Accordion>
								<LandingAccordion title="Hogyan csökkenthető a törlesztőrészletem?">
									<p>
										Az OTP bank lakáshiteleihez kapcsolódik egy úgynevezett “egyszeri
										kamatcsökkentési szolgáltatás”. Ez azt jelenti, hogy a futamidő 121. hónapjától
										kezdődően egy alkalommal díjmentesen kérheted a kamatok módosítását.
										<br />
										Ehhez nem szükséges hitelkérelmi dokumentációt benyújtanod és nem jár
										szerződésmódosítási díjjal sem.
										<br />A lehetőséget akkor érdemes kihasználni, amikor a piaci kamatkörnyezet
										kedvező irányba mozdul, ugyanis az új kamat egy piaci mutató, a BIRS aktuális
										értékéhez van kötve.
									</p>
									<p>
										Természetesen van lehetőség más módon is csökkenteni a törlesztőrészletet.
										Bármikor megnézheted, hogy milyen feltételekkel válthatod ki meglévő kölcsönödet
										az OTP Banknál, vagy más pénzintézetnél. A hitelkiváltás azonban gyakorlatilag
										egy új hiteligénylést jelent, az ehhez kapcsolódó folyamaton végig kell menni.
									</p>
								</LandingAccordion>
							</Accordion>
						</Col>
					</Row>
				</LandingContainer>
			);
		}

		return null;
	}
}
